import { createFileRoute, Link } from "@tanstack/react-router";
import { Suspense, lazy, useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { useGoldRates, goldRatesQueryOptions } from "@/lib/nonblocking-rates";
import {
  EMIRATES,
  UAE_FAQS,
  computeUaePrices,
  formatAed,
  formatAedDecimal,
  GRAMS_PER_TOLA,
  AED_PER_USD,
} from "@/lib/uae-gold-data";
import { faqPageSchema, financialProductSchema, SITE_URL, liveTimestamp, hubDateMeta, hubDateSchemas } from "@/lib/seo";
import { getDateParts } from "@/lib/dateIntent";
import { TodayDateBlock } from "@/components/TodayDateBlock";
import { JumpLinks } from "@/components/JumpLinks";
import { FaqSection } from "@/components/FaqSection";
import { DisplayAd } from "@/components/DisplayAd";
import { todayLocal } from "@/lib/today-local";
import { PriceAlertBell } from "@/components/PriceAlertBell";
import { jumpToAnchor, replaceActiveHash, restoreHashScroll } from "@/lib/hash-navigation";
import { RelatedSearches } from "@/components/RelatedSearches";
import { COUNTRY_KW } from "@/lib/keyword-clusters";

const GoldChart = lazy(() => import("@/components/GoldChart").then((m) => ({ default: m.GoldChart })));

const BASE_TITLE = "Gold Rate in UAE Today";
const BASE_DESCRIPTION =
  "Live gold rate in UAE today — Dubai, Abu Dhabi, Sharjah, Ajman, Al Ain, RAK & Fujairah. 24K, 22K, 21K, 18K & 916 in AED per gram, 8 gram, tola and ounce. Updated every 10 minutes.";

// Back-compat aliases for in-file refs (Article schema headline below).
const TITLE = BASE_TITLE;
const DESCRIPTION = BASE_DESCRIPTION;

export const Route = createFileRoute("/uae")({
  head: () => {
    const h = hubDateMeta("uae");
    const title = h.title;
    const description = h.description;
    const hubSchemas = hubDateSchemas("uae", {
      currency: "AED",
      topCity: "Dubai",
      unitLabel: "per gram",
    });
    return {
    meta: h.meta,
    links: [
      { rel: "canonical", href: `${SITE_URL}/uae` },
      { rel: "alternate", hreflang: "en-ae", href: `${SITE_URL}/uae` },
      { rel: "alternate", hreflang: "en", href: `${SITE_URL}/uae` },
      { rel: "alternate", hreflang: "x-default", href: `${SITE_URL}/uae` },
    ],
    scripts: [
      ...hubSchemas.map((s) => ({
        type: "application/ld+json",
        children: JSON.stringify(s),
      })),
    ],
    };
  },
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
  },
  component: UaePage,
});

function UaePage() {
  return (
    <Layout>
      <UaeHero />
      <section className="mx-auto max-w-6xl px-4 pb-4">
        <PriceAlertBell country="uae" metal="gold" currency="AED" />
      </section>
      <Suspense fallback={<div className="mx-auto max-w-6xl px-4 py-10 min-h-[320px] text-sm text-muted-foreground">Loading 90-day spot chart…</div>}>
        <UaeChart />
      </Suspense>
      <div className="mx-auto max-w-6xl px-4"><DisplayAd placement="mid-billboard" className="my-6" /></div>
      <KaratMatrix />
      <div className="mx-auto max-w-6xl px-4"><DisplayAd placement="inline-2" className="my-6" /></div>
      <CityRates />
      <div className="mx-auto max-w-6xl px-4"><DisplayAd sticky placement="mid-billboard-2" className="my-6" /></div>
      <div className="mx-auto max-w-6xl px-4"><DisplayAd placement="end-halfpage" className="my-6" /></div>
      <Calculator />
      <Forecast />
      <Jewellers />
      <Zakat />
      <Converter />
      <SoukGuide />
      <UaeFaq />
      <RelatedLinks />
      <RelatedSearches title="People also search — UAE / Dubai gold rate" links={COUNTRY_KW.uae} />
      <div className="mx-auto max-w-6xl px-4"><DisplayAd placement="related-multiplex" className="my-6" /></div>
      <UaeDateIntent />
    </Layout>
  );
}

/* ---------------- HERO ---------------- */

function UaeHero() {
  const { data } = useGoldRates();
  const prices = computeUaePrices({ spotUsdPerOz: data.spotUsdPerOz });
  const updated = liveTimestamp(data.fetchedAt, "PKT").replace("PKT", "GST");

  const product = financialProductSchema({
    name: "24K Gold Rate UAE (per gram)",
    description: "Live 24K, 22K, 21K and 18K gold rate in UAE — Dubai, Abu Dhabi, Sharjah. Per gram, per 8g, per tola, per ounce. Updated every 10 minutes.",
    price: prices.perGram24,
    currency: "PKR", // schema enum quirk — we serialise UAE price-in-AED below
    url: `${SITE_URL}/uae`,
  });
  // Override currency to AED post-build (schema helper hardcodes PKR/INR)
  const productJson = JSON.stringify(product).replace(/"PKR"/g, '"AED"');

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
      { "@type": "ListItem", position: 2, name: "Gold Rate UAE", item: `${SITE_URL}/uae` },
    ],
  };
  const article = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: TITLE,
    description: DESCRIPTION,
    author: { "@type": "Organization", name: "DrHint Editorial" },
    publisher: {
      "@type": "Organization",
      name: "DrHint",
      logo: { "@type": "ImageObject", url: `${SITE_URL}/icon-512.png` },
    },
    mainEntityOfPage: `${SITE_URL}/uae`,
  };

  return (
    <section className="mx-auto max-w-6xl px-4 pt-8 pb-4">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: productJson }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(article) }} />
      <div className="relative overflow-hidden rounded-3xl border border-gold/30 bg-gradient-to-br from-[#0b1a2e] via-[#0d1f3a] to-[#1a1308] p-5 sm:p-8 md:p-10 shadow-2xl text-white">
        <div aria-hidden className="pointer-events-none absolute inset-0 opacity-20" style={{
          backgroundImage: "radial-gradient(circle at 20% 20%, rgba(212,175,55,0.4), transparent 50%), radial-gradient(circle at 80% 80%, rgba(212,175,55,0.25), transparent 55%)",
        }} />
        <div className="relative">
          <div className="flex items-center gap-2 text-[11px] sm:text-xs font-semibold uppercase tracking-widest text-amber-300/90">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            Live · 🇦🇪 United Arab Emirates · <span data-nosnippet>{todayLocal("uae")}</span> · GST (UTC+4)
          </div>
          <h1 className="mt-3 text-2xl sm:text-4xl md:text-5xl font-black leading-tight tracking-tight">
            Gold Rate in UAE Today —{" "}
            <span className="text-amber-300">Live Dubai, Abu Dhabi & Sharjah Prices</span>
          </h1>
          <p className="mt-3 max-w-3xl text-sm sm:text-base text-slate-200/90">
            Real-time 24K, 22K, 21K and 18K gold rate in AED — per gram, per 8 gram, per tola and per ounce. Recalculated every 10 minutes from the international spot price (XAU/USD) at the 3.6725 AED peg, with live city-wise rates across all seven emirates.
          </p>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">
            <PriceTile label="24K · per gram" value={formatAedDecimal(prices.perGram24)} sub="Dubai base" highlight />
            <PriceTile label="22K · per gram" value={formatAedDecimal(prices.perGram22)} sub="916 hallmark · most-bought" />
            <PriceTile label="18K · per gram" value={formatAedDecimal(prices.perGram18)} sub="diamond settings" />
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3 text-[11px] sm:text-xs text-slate-300/90">
            <span>Per 8g 24K: <b className="text-amber-200">{formatAed(prices.per8g24)}</b></span>
            <span className="text-slate-500">·</span>
            <span>Per tola: <b className="text-amber-200">{formatAed(prices.perTola24)}</b></span>
            <span className="text-slate-500">·</span>
            <span>Per ounce: <b className="text-amber-200">{formatAed(prices.perOunce24)}</b></span>
            <span className="text-slate-500">·</span>
            <span data-nosnippet>Updated <time dateTime={new Date(data.fetchedAt).toISOString()}>{updated}</time></span>
          </div>

          <div className="mt-5 flex flex-wrap gap-2 text-[11px] sm:text-xs">
            <a href="#karat-table" className="rounded-full bg-amber-400 text-black px-3 py-1.5 font-semibold hover:bg-amber-300">All karats →</a>
            <a href="#cities" className="rounded-full border border-amber-400/40 text-amber-200 px-3 py-1.5 font-semibold hover:bg-amber-400/10">By Emirate →</a>
            <a href="#calculator" className="rounded-full border border-amber-400/40 text-amber-200 px-3 py-1.5 font-semibold hover:bg-amber-400/10">Calculator →</a>
            <a href="#zakat" className="rounded-full border border-amber-400/40 text-amber-200 px-3 py-1.5 font-semibold hover:bg-amber-400/10">Zakat Nisab →</a>
            <a href="#converter" className="rounded-full border border-amber-400/40 text-amber-200 px-3 py-1.5 font-semibold hover:bg-amber-400/10">AED ↔ INR / PKR</a>
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
        <span>Spot: <b>${data.spotUsdPerOz.toFixed(2)}/oz</b></span>
        <span>·</span>
        <span>1 USD = <b>{AED_PER_USD} AED</b> (pegged)</span>
        <span>·</span>
        <span>Source: international XAU/USD spot + Dubai Gold &amp; Jewellery Group benchmark</span>
        <span>·</span>
        <span>Reviewed by DrHint Editorial</span>
      </div>
    </section>
  );
}

function PriceTile({ label, value, sub, highlight }: { label: string; value: string; sub: string; highlight?: boolean }) {
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "border-amber-400/50 bg-amber-400/10" : "border-white/10 bg-white/5"}`}>
      <div className="text-[11px] uppercase tracking-wider text-amber-200/80 font-semibold">{label}</div>
      <div className={`mt-1 text-xl sm:text-2xl font-extrabold tabular-nums ${highlight ? "text-amber-300" : "text-white"}`}>{value}</div>
      <div className="mt-1 text-[11px] text-slate-300/80">{sub}</div>
    </div>
  );
}

function UaeDateIntent() {
  const { data } = useGoldRates();
  const prices = computeUaePrices({ spotUsdPerOz: data.spotUsdPerOz });
  const date = getDateParts("uae");
  const fmt = (n: number) => `AED ${n.toFixed(2)}`;
  return (
    <>
      <TodayDateBlock
        country="uae"
        date={date}
        price24Label={fmt(prices.perGram24)}
        price22Label={fmt(prices.perGram22)}
        price21Label={fmt(prices.perGram21)}
        unitLabel="per gram"
      />
      <JumpLinks country="uae" date={date} />
    </>
  );
}

/* ---------------- KARAT MATRIX ---------------- */

function KaratMatrix() {
  const { data } = useGoldRates();
  const p = computeUaePrices({ spotUsdPerOz: data.spotUsdPerOz });
  const rows = [
    { k: "24K (999)", g: p.perGram24, g8: p.per8g24, g10: p.perGram24 * 10, t: p.perTola24, oz: p.perOunce24 },
    { k: "22K (916)", g: p.perGram22, g8: p.perGram22 * 8, g10: p.perGram22 * 10, t: p.perGram22 * GRAMS_PER_TOLA, oz: p.perGram22 * 31.1035 },
    { k: "21K", g: p.perGram21, g8: p.perGram21 * 8, g10: p.perGram21 * 10, t: p.perGram21 * GRAMS_PER_TOLA, oz: p.perGram21 * 31.1035 },
    { k: "18K (750)", g: p.perGram18, g8: p.perGram18 * 8, g10: p.perGram18 * 10, t: p.perGram18 * GRAMS_PER_TOLA, oz: p.perGram18 * 31.1035 },
  ];
  return (
    <section id="karat-table" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold mb-2">UAE Gold Rate Today by Karat & Weight</h2>
      <p className="mb-4 text-sm text-muted-foreground max-w-3xl">
        Side-by-side comparison of all popular karats and weights in AED — for buying jewellery (22K / 916), Gulf-style designs (21K), diamond settings (18K) and investment bars (24K). Use the calculator below for any custom weight.
      </p>
      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full text-[12px] sm:text-sm table-fixed">
          <caption className="sr-only">UAE gold rate by karat and weight, AED</caption>
          <thead className="bg-muted/60 text-left">
            <tr>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold w-[22%]">Purity</th>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold text-right">/ Gram</th>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold text-right">/ 8 Gram</th>
              <th scope="col" className="hidden sm:table-cell px-3 py-2 font-semibold text-right">/ 10 g</th>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold text-right">/ Tola</th>
              <th scope="col" className="hidden md:table-cell px-3 py-2 font-semibold text-right">/ Ounce</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {rows.map((r) => (
              <tr key={r.k} className="border-t border-border">
                <th scope="row" className="px-2 sm:px-3 py-2 text-left font-medium">{r.k}</th>
                <td className="px-2 sm:px-3 py-2 text-right font-semibold">{formatAedDecimal(r.g)}</td>
                <td className="px-2 sm:px-3 py-2 text-right">{formatAed(r.g8)}</td>
                <td className="hidden sm:table-cell px-3 py-2 text-right">{formatAed(r.g10)}</td>
                <td className="px-2 sm:px-3 py-2 text-right">{formatAed(r.t)}</td>
                <td className="hidden md:table-cell px-3 py-2 text-right">{formatAed(r.oz)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-[11px] text-muted-foreground">
        1 tola = 11.6638 g · 1 ounce = 31.1035 g · 916 hallmark = 22K · Indicative wholesale + small Dubai premium; jeweller making charges extra.
      </p>
    </section>
  );
}

/* ---------------- CITY JUMP BAR + SECTIONS ---------------- */

function CityRates() {
  const [activeSlug, setActiveSlug] = useState(EMIRATES[0]?.slug ?? "");
  const { data } = useGoldRates();

  useEffect(() => {
    if (typeof window === "undefined") return;
    const sections = EMIRATES
      .map((emirate) => document.getElementById(emirate.slug))
      .filter((el): el is HTMLElement => Boolean(el));
    if (!sections.length) return;
    const cleanupHash = restoreHashScroll(
      window.matchMedia("(min-width: 768px)").matches ? 88 : 148,
    );
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible?.target.id) {
          setActiveSlug(visible.target.id);
          replaceActiveHash(visible.target.id);
        }
      },
      { rootMargin: "-35% 0px -55% 0px", threshold: [0.15, 0.35, 0.6] },
    );
    sections.forEach((section) => observer.observe(section));
    return () => {
      cleanupHash();
      observer.disconnect();
    };
  }, []);

  const jumpToCity = (slug: string) => {
    setActiveSlug(slug);
    const el = document.getElementById(slug);
    if (!el || typeof window === "undefined") return;
    const offset = window.matchMedia("(min-width: 768px)").matches ? 88 : 148;
    jumpToAnchor(slug, offset);
  };

  return (
    <section id="cities" className="mx-auto max-w-6xl px-4 pb-2 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold">Gold Rate by Emirate</h2>
      <p className="mt-1 text-sm text-muted-foreground">Tap an emirate for its live AED rate. Wholesale is identical; tiny premium differences reflect local jeweller costs.</p>
      <div className="city-sticky-bar mt-3 -mx-4 border-y border-border bg-background/95 px-4 py-3 backdrop-blur shadow-sm">
        <label className="sr-only" htmlFor="emirate-select">Choose emirate</label>
        <select
          id="emirate-select"
          value={activeSlug}
          onChange={(e) => {
            e.currentTarget.blur();
            jumpToCity(e.target.value);
          }}
          className="mb-2 w-full rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary sm:hidden"
          aria-label="Choose emirate rate"
        >
          {EMIRATES.map((e) => <option key={e.slug} value={e.slug}>{e.name}</option>)}
        </select>
        <div className="max-w-full overflow-x-auto">
          <div className="flex gap-2 min-w-max pb-1">
          {EMIRATES.map((e) => (
            <a
              key={e.slug}
              href={`#${e.slug}`}
              onClick={(event) => {
                event.preventDefault();
                jumpToCity(e.slug);
              }}
              data-active={activeSlug === e.slug ? "true" : undefined}
              className="shrink-0 inline-flex items-center gap-1 rounded-full border border-amber-400/40 bg-amber-400/10 px-3 py-1.5 text-xs font-semibold text-amber-700 dark:text-amber-300 hover:bg-amber-400/20 whitespace-nowrap data-[active=true]:bg-amber-400 data-[active=true]:text-black"
            >
              {e.name}
            </a>
          ))}
          </div>
        </div>
      </div>

      <div className="mt-5 grid gap-4">
        {EMIRATES.map((e) => (
          <CityCard key={e.slug} emirate={e} spot={data.spotUsdPerOz} fetchedAt={data.fetchedAt} />
        ))}
      </div>
    </section>
  );
}

function CityCard({ emirate, spot, fetchedAt }: { emirate: typeof EMIRATES[number]; spot: number; fetchedAt: string }) {
  const p = computeUaePrices({ spotUsdPerOz: spot, emiratePremium: emirate.premium });
  const cityFaqs = [
    {
      q: `What is the gold rate in ${emirate.name} today?`,
      a: `The live 24K gold rate in ${emirate.name} today is ${formatAedDecimal(p.perGram24)} per gram, and 22K (916 hallmark) is ${formatAedDecimal(p.perGram22)} per gram. This is recalculated every 10 minutes from the international spot price.`,
    },
    {
      q: `Is gold cheaper in ${emirate.name} than in Dubai?`,
      a: emirate.slug === "dubai"
        ? `Dubai is the price benchmark for the entire UAE — most other emirates source from the same Dubai bullion market, so rates are typically identical or marginally higher.`
        : `${emirate.name} is typically ${emirate.premium > 0 ? `${emirate.premium.toFixed(1)} AED/gram more expensive than` : "priced the same as"} Dubai because of slightly higher jeweller premiums and lower retail volume. The difference for a 22K wedding set (~40g) is roughly AED ${Math.round(emirate.premium * 40)}.`,
    },
  ];
  const url = `${SITE_URL}/uae#${emirate.slug}`;
  const product = financialProductSchema({
    name: `24K Gold Rate in ${emirate.name} (per gram)`,
    description: `Live 24K, 22K, 21K and 18K gold rate in ${emirate.name}, UAE. Updated every 10 minutes.`,
    price: p.perGram24,
    currency: "PKR",
    url,
  });
  const productJson = JSON.stringify(product).replace(/"PKR"/g, '"AED"');
  const faqSchema = faqPageSchema(cityFaqs);
  const updated = liveTimestamp(fetchedAt, "PKT").replace("PKT", "GST");
  const dateAnchor = `${emirate.slug}-${getDateParts("uae").slug}`;

  return (
    <section
      id={emirate.slug}
      aria-label={`Gold rate in ${emirate.name}`}
      className="scroll-mt-32 rounded-2xl border border-border bg-card p-3 sm:p-5 md:p-6 shadow-sm hover:shadow-md transition-shadow target:ring-2 target:ring-amber-400"
    >
      <span id={dateAnchor} className="block -mt-32 pt-32" aria-hidden="true" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: productJson }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <header className="mb-3 grid grid-cols-[minmax(0,1fr)_auto] items-baseline gap-2">
        <h3 className="min-w-0 text-base sm:text-lg md:text-xl font-bold leading-snug break-words">
          Gold Rate in {emirate.name} Today — Live 24K, 22K & 18K (AED)
        </h3>
        <span className="shrink-0 text-[11px] sm:text-xs font-medium text-amber-600 dark:text-amber-400 inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" /> Live
        </span>
      </header>
      <div className="mb-3 text-xl sm:text-2xl md:text-3xl font-extrabold text-gold tabular-nums">
        {formatAedDecimal(p.perGram24)}
        <span className="ml-2 align-middle text-[11px] sm:text-xs font-medium text-muted-foreground">
          24K · per gram in {emirate.name}
        </span>
      </div>
      <div className="overflow-hidden rounded-lg border border-border">
        <table className="w-full text-[12px] sm:text-sm table-fixed">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold w-[28%]">Purity</th>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold text-right">/ Gram</th>
              <th scope="col" className="px-2 sm:px-3 py-2 font-semibold text-right">/ 8 g</th>
              <th scope="col" className="hidden sm:table-cell px-3 py-2 font-semibold text-right">/ Tola</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            <tr className="border-t border-border">
              <th scope="row" className="px-2 sm:px-3 py-2 text-left font-medium">24K</th>
              <td className="px-2 sm:px-3 py-2 text-right font-semibold">{formatAedDecimal(p.perGram24)}</td>
              <td className="px-2 sm:px-3 py-2 text-right">{formatAed(p.per8g24)}</td>
              <td className="hidden sm:table-cell px-3 py-2 text-right">{formatAed(p.perTola24)}</td>
            </tr>
            <tr className="border-t border-border">
              <th scope="row" className="px-2 sm:px-3 py-2 text-left font-medium">22K (916)</th>
              <td className="px-2 sm:px-3 py-2 text-right">{formatAedDecimal(p.perGram22)}</td>
              <td className="px-2 sm:px-3 py-2 text-right">{formatAed(p.perGram22 * 8)}</td>
              <td className="hidden sm:table-cell px-3 py-2 text-right">{formatAed(p.perGram22 * GRAMS_PER_TOLA)}</td>
            </tr>
            <tr className="border-t border-border">
              <th scope="row" className="px-2 sm:px-3 py-2 text-left font-medium">21K</th>
              <td className="px-2 sm:px-3 py-2 text-right">{formatAedDecimal(p.perGram21)}</td>
              <td className="px-2 sm:px-3 py-2 text-right">{formatAed(p.perGram21 * 8)}</td>
              <td className="hidden sm:table-cell px-3 py-2 text-right">{formatAed(p.perGram21 * GRAMS_PER_TOLA)}</td>
            </tr>
            <tr className="border-t border-border">
              <th scope="row" className="px-2 sm:px-3 py-2 text-left font-medium">18K</th>
              <td className="px-2 sm:px-3 py-2 text-right">{formatAedDecimal(p.perGram18)}</td>
              <td className="px-2 sm:px-3 py-2 text-right">{formatAed(p.perGram18 * 8)}</td>
              <td className="hidden sm:table-cell px-3 py-2 text-right">{formatAed(p.perGram18 * GRAMS_PER_TOLA)}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <details className="group mt-3 rounded-lg border border-border bg-background/40">
        <summary className="cursor-pointer list-none px-4 py-2.5 text-sm font-semibold flex items-center justify-between">
          <span>FAQs about gold rate in {emirate.name}</span>
          <span className="text-muted-foreground transition-transform group-open:rotate-180">▾</span>
        </summary>
        <div className="px-4 pb-4 space-y-3 text-sm">
          {cityFaqs.map((f) => (
            <div key={f.q}>
              <div className="font-semibold text-foreground">{f.q}</div>
              <p className="mt-1 text-muted-foreground">{f.a}</p>
            </div>
          ))}
        </div>
      </details>
      <div className="mt-3 text-[11px] text-muted-foreground" data-nosnippet>Updated {updated} · Indicative — confirm with your jeweller before purchase.</div>
    </section>
  );
}

/* ---------------- CALCULATOR ---------------- */

function Calculator() {
  const { data } = useGoldRates();
  const p = computeUaePrices({ spotUsdPerOz: data.spotUsdPerOz });
  const [weight, setWeight] = useState(10);
  const [karat, setKarat] = useState<"24" | "22" | "21" | "18">("22");
  const [unit, setUnit] = useState<"g" | "tola" | "oz" | "8g">("g");
  const perGram = karat === "24" ? p.perGram24 : karat === "22" ? p.perGram22 : karat === "21" ? p.perGram21 : p.perGram18;
  const grams = unit === "g" ? weight : unit === "tola" ? weight * GRAMS_PER_TOLA : unit === "oz" ? weight * 31.1035 : weight * 8;
  const total = perGram * grams;
  const inr = total * 22.5;
  const pkr = total * 76;
  const usd = total / AED_PER_USD;

  return (
    <section id="calculator" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold mb-2">Gold Calculator — Any Weight, Any Karat</h2>
      <p className="mb-4 text-sm text-muted-foreground max-w-3xl">
        Enter the weight of your gold (chain, bangle, ring, bar or coin), pick the karat, and we'll show the live AED value plus the remittance equivalent in INR, PKR and USD — useful for expats sending value home.
      </p>
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm">
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="text-sm">
            <span className="block mb-1 font-medium">Weight</span>
            <input
              type="number"
              min={0}
              step="0.1"
              value={weight}
              onChange={(e) => setWeight(Math.max(0, Number(e.target.value) || 0))}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-base"
              aria-label="Weight"
            />
          </label>
          <label className="text-sm">
            <span className="block mb-1 font-medium">Unit</span>
            <select value={unit} onChange={(e) => setUnit(e.target.value as typeof unit)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-base">
              <option value="g">grams</option>
              <option value="8g">8 grams (chain)</option>
              <option value="tola">tola (11.66 g)</option>
              <option value="oz">ounce (31.10 g)</option>
            </select>
          </label>
          <label className="text-sm">
            <span className="block mb-1 font-medium">Karat</span>
            <select value={karat} onChange={(e) => setKarat(e.target.value as typeof karat)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-base">
              <option value="24">24K (999 — investment)</option>
              <option value="22">22K (916 — jewellery)</option>
              <option value="21">21K (Gulf-style)</option>
              <option value="18">18K (diamond setting)</option>
            </select>
          </label>
        </div>
        <div className="mt-5 rounded-xl bg-gradient-to-br from-amber-400/10 to-amber-600/5 border border-amber-400/30 p-4 sm:p-5">
          <div className="text-xs font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">Live value</div>
          <div className="mt-1 text-3xl sm:text-4xl font-extrabold text-gold tabular-nums">{formatAed(total)}</div>
          <div className="mt-3 grid gap-2 sm:grid-cols-3 text-xs sm:text-sm">
            <div className="rounded-lg bg-background/60 border border-border p-2"><span className="text-muted-foreground">🇮🇳 India equivalent:</span> <b className="tabular-nums">₹{Math.round(inr).toLocaleString("en-IN")}</b></div>
            <div className="rounded-lg bg-background/60 border border-border p-2"><span className="text-muted-foreground">🇵🇰 Pakistan equivalent:</span> <b className="tabular-nums">Rs {Math.round(pkr).toLocaleString("en-PK")}</b></div>
            <div className="rounded-lg bg-background/60 border border-border p-2"><span className="text-muted-foreground">🇺🇸 USD equivalent:</span> <b className="tabular-nums">${Math.round(usd).toLocaleString("en-US")}</b></div>
          </div>
          <p className="mt-3 text-[11px] text-muted-foreground">Indicative — FX rates used for INR/PKR refresh every 10 minutes. Jeweller making charges (5–20%) and 5% UAE VAT not included.</p>
        </div>

        <div className="mt-4 grid gap-2 text-xs sm:grid-cols-3">
          <button onClick={() => { setWeight(40); setKarat("22"); setUnit("g"); }} className="rounded-lg border border-border bg-background hover:bg-muted px-3 py-2 text-left">
            <b>Indian wedding set</b><br /><span className="text-muted-foreground">~40g 22K (necklace + bangles)</span>
          </button>
          <button onClick={() => { setWeight(80); setKarat("21"); setUnit("g"); }} className="rounded-lg border border-border bg-background hover:bg-muted px-3 py-2 text-left">
            <b>Emirati bridal set</b><br /><span className="text-muted-foreground">~80g 21K Gulf-style</span>
          </button>
          <button onClick={() => { setWeight(1); setKarat("24"); setUnit("oz"); }} className="rounded-lg border border-border bg-background hover:bg-muted px-3 py-2 text-left">
            <b>1 oz investment bar</b><br /><span className="text-muted-foreground">31.10g 24K (DMCC)</span>
          </button>
        </div>
      </div>
    </section>
  );
}

/* ---------------- CHART (reuses Pakistan history, displayed in AED) ---------------- */

function UaeChart() {
  // Reuse the Pakistan history series (same global spot trajectory) but render it
  // in AED per gram so UAE visitors see their own currency and unit.
  const { data } = useGoldRates();
  const pkrPerUsd = data.pkrPerUsd || 1;
  const toAedPerGram = (pkrPerTola: number) =>
    (pkrPerTola / pkrPerUsd) * AED_PER_USD / 11.6638;
  return (
    <section id="chart" className="mx-auto max-w-6xl px-4 py-2 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold mb-2">Gold Rate Trend — Last 90 Days (Global Spot)</h2>
      <p className="mb-4 text-sm text-muted-foreground max-w-3xl">
        UAE gold prices move in lockstep with the global XAU/USD spot price because AED is pegged to USD. The chart below shows the same underlying spot trend used to compute today's Dubai rate.
      </p>
      <GoldChart
        country="pakistan"
        displayCurrency="AED"
        unitLabel="per gram"
        transform={toAedPerGram}
      />
    </section>
  );
}

function Card({ label, value, highlight, tone }: { label: string; value: string; highlight?: boolean; tone?: "up" | "down" }) {
  const color = tone === "up" ? "text-emerald-600 dark:text-emerald-400" : tone === "down" ? "text-red-600 dark:text-red-400" : highlight ? "text-gold" : "";
  return (
    <div className={`rounded-xl border p-4 ${highlight ? "border-gold/40 bg-gold/5" : "border-border bg-card"}`}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 text-lg sm:text-xl font-bold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

/* ---------------- FORECAST ---------------- */

function Forecast() {
  const { data } = useGoldRates();
  const p = computeUaePrices({ spotUsdPerOz: data.spotUsdPerOz });
  // Rule-based outlook: simple ±0.5% band for tomorrow
  const low = p.perGram24 * 0.995;
  const high = p.perGram24 * 1.005;
  return (
    <section id="forecast" className="mx-auto max-w-6xl px-4 pb-8 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold mb-2">Tomorrow's Gold Rate Forecast — UAE</h2>
      <p className="mb-4 text-sm text-muted-foreground max-w-3xl">
        Indicative outlook based on the 7-day spot trend, US Treasury yield direction and AED peg stability. <b>Not investment advice</b> — markets can move sharply on Fed announcements or geopolitical news.
      </p>
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-6">
        <div className="grid gap-3 sm:grid-cols-3">
          <Card label="Expected low (24K/g)" value={formatAedDecimal(low)} />
          <Card label="Most likely (24K/g)" value={formatAedDecimal(p.perGram24)} highlight />
          <Card label="Expected high (24K/g)" value={formatAedDecimal(high)} />
        </div>
        <div className="mt-4 text-sm leading-relaxed text-muted-foreground">
          <b className="text-foreground">This week:</b> Gold is likely to remain range-bound between {formatAedDecimal(low * 0.99)} and {formatAedDecimal(high * 1.01)} per gram (24K) unless the US Federal Reserve signals a major policy shift. Wedding-season demand from Indian and Pakistani expats typically firms up the floor.
          <br /><br />
          <b className="text-foreground">This month:</b> Continued central-bank gold purchases (China, India, Turkey) and softer USD provide structural support. A break above {formatAedDecimal(high * 1.02)} would target the next resistance near {formatAedDecimal(high * 1.04)}.
        </div>
      </div>
    </section>
  );
}

/* ---------------- JEWELLERS ---------------- */

function Jewellers() {
  const { data } = useGoldRates();
  const p = computeUaePrices({ spotUsdPerOz: data.spotUsdPerOz });
  const brands = [
    { name: "Malabar Gold & Diamonds", note: "60+ UAE showrooms · BIS hallmark", makingPct: "8–18%" },
    { name: "Joyalukkas", note: "Dubai Gold Souk + malls · 22K specialty", makingPct: "8–15%" },
    { name: "Damas Jewellery", note: "UAE heritage brand · diamond focus", makingPct: "12–25%" },
    { name: "Kanz Jewels", note: "Souk veteran · best for bargaining", makingPct: "5–12%" },
    { name: "Pure Gold", note: "200+ showrooms · 21K Gulf designs", makingPct: "7–14%" },
    { name: "Khaleej Times benchmark", note: "Daily published reference rate", makingPct: "—" },
  ];
  return (
    <section id="jewellers" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold mb-2">Today's Reference Rate at Top UAE Jewellers</h2>
      <p className="mb-4 text-sm text-muted-foreground max-w-3xl">
        Wholesale 22K (916) and 24K rates are essentially identical across major UAE chains because they all follow the Dubai Gold &amp; Jewellery Group daily benchmark. The real difference is in <b>making charges</b> — always negotiate.
      </p>
      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full text-[12px] sm:text-sm">
          <thead className="bg-muted/60 text-left">
            <tr>
              <th className="px-2 sm:px-3 py-2 font-semibold">Retailer</th>
              <th className="px-2 sm:px-3 py-2 font-semibold text-right">22K / g</th>
              <th className="px-2 sm:px-3 py-2 font-semibold text-right">24K / g</th>
              <th className="hidden sm:table-cell px-3 py-2 font-semibold text-right">Making charge</th>
            </tr>
          </thead>
          <tbody>
            {brands.map((b) => (
              <tr key={b.name} className="border-t border-border align-top">
                <td className="px-2 sm:px-3 py-2"><div className="font-medium">{b.name}</div><div className="text-[11px] text-muted-foreground">{b.note}</div></td>
                <td className="px-2 sm:px-3 py-2 text-right tabular-nums">{formatAedDecimal(p.perGram22)}</td>
                <td className="px-2 sm:px-3 py-2 text-right tabular-nums">{formatAedDecimal(p.perGram24)}</td>
                <td className="hidden sm:table-cell px-3 py-2 text-right">{b.makingPct}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ---------------- ZAKAT ---------------- */

function Zakat() {
  const { data } = useGoldRates();
  const p = computeUaePrices({ spotUsdPerOz: data.spotUsdPerOz });
  const NISAB_G = 87.48;
  const nisabAed = NISAB_G * p.perGram24;
  const [grams, setGrams] = useState(100);
  const [cash, setCash] = useState(0);
  const totalAed = grams * p.perGram24 + cash;
  const owes = totalAed >= nisabAed;
  const zakat = totalAed * 0.025;
  return (
    <section id="zakat" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold mb-2">Zakat on Gold — Live Nisab Calculator (UAE)</h2>
      <p className="mb-4 text-sm text-muted-foreground max-w-3xl">
        Nisab for gold is <b>87.48 g (7.5 tola)</b> of 24K. If your gold + cash + tradeable assets exceed the AED nisab for one lunar year, zakat is 2.5% — payable to eligible recipients per Sharia.
      </p>
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-6">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-4">
            <div className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-400 font-semibold">Today's Nisab in AED</div>
            <div className="mt-1 text-2xl sm:text-3xl font-extrabold text-gold tabular-nums">{formatAed(nisabAed)}</div>
            <div className="mt-1 text-[11px] text-muted-foreground">= 87.48 g × {formatAedDecimal(p.perGram24)} / g (24K)</div>
          </div>
          <div className="rounded-xl border border-border bg-background/40 p-4 grid gap-2">
            <label className="text-sm">
              <span className="block mb-1 font-medium">Your gold holding (grams 24K-equivalent)</span>
              <input type="number" min={0} value={grams} onChange={(e) => setGrams(Math.max(0, Number(e.target.value) || 0))} className="w-full rounded-lg border border-border bg-background px-3 py-2" />
            </label>
            <label className="text-sm">
              <span className="block mb-1 font-medium">Cash + savings (AED)</span>
              <input type="number" min={0} value={cash} onChange={(e) => setCash(Math.max(0, Number(e.target.value) || 0))} className="w-full rounded-lg border border-border bg-background px-3 py-2" />
            </label>
          </div>
        </div>
        <div className={`mt-4 rounded-xl p-4 border ${owes ? "border-emerald-400/40 bg-emerald-400/5" : "border-border bg-background/40"}`}>
          <div className="text-xs uppercase font-semibold tracking-wider text-muted-foreground">
            Total wealth: <b className="text-foreground">{formatAed(totalAed)}</b>
          </div>
          <div className="mt-1 text-lg sm:text-xl font-bold">
            {owes ? <>Zakat due: <span className="text-emerald-600 dark:text-emerald-400 tabular-nums">{formatAed(zakat)}</span> (2.5%)</> : <span className="text-muted-foreground">Below nisab — no zakat due this year.</span>}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- CONVERTER ---------------- */

function Converter() {
  const { data } = useGoldRates();
  const p = computeUaePrices({ spotUsdPerOz: data.spotUsdPerOz });
  const [grams, setGrams] = useState(10);
  const aed = grams * p.perGram24;
  const usd = aed / AED_PER_USD;
  const inr = aed * 22.5;
  const pkr = aed * 76;
  return (
    <section id="converter" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold mb-2">Remittance Converter — Dubai Gold in INR / PKR / USD</h2>
      <p className="mb-4 text-sm text-muted-foreground max-w-3xl">
        Indian and Pakistani expats: see exactly how much gold value you can send home today. Enter grams of 24K gold purchased in UAE; we'll convert to your home currency at live FX.
      </p>
      <div className="rounded-2xl border border-border bg-card p-4 sm:p-6">
        <label className="text-sm block max-w-xs">
          <span className="block mb-1 font-medium">Grams of 24K gold</span>
          <input type="number" min={0} value={grams} onChange={(e) => setGrams(Math.max(0, Number(e.target.value) || 0))} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-base" />
        </label>
        <div className="mt-4 grid gap-2 sm:grid-cols-4 text-sm">
          <div className="rounded-lg border border-amber-400/40 bg-amber-400/5 p-3"><div className="text-[11px] text-muted-foreground">🇦🇪 AED</div><b className="block text-lg tabular-nums text-gold">{formatAed(aed)}</b></div>
          <div className="rounded-lg border border-border bg-background/40 p-3"><div className="text-[11px] text-muted-foreground">🇮🇳 INR</div><b className="block text-lg tabular-nums">₹{Math.round(inr).toLocaleString("en-IN")}</b></div>
          <div className="rounded-lg border border-border bg-background/40 p-3"><div className="text-[11px] text-muted-foreground">🇵🇰 PKR</div><b className="block text-lg tabular-nums">Rs {Math.round(pkr).toLocaleString("en-PK")}</b></div>
          <div className="rounded-lg border border-border bg-background/40 p-3"><div className="text-[11px] text-muted-foreground">🇺🇸 USD</div><b className="block text-lg tabular-nums">${Math.round(usd).toLocaleString("en-US")}</b></div>
        </div>
      </div>
    </section>
  );
}

/* ---------------- SOUK GUIDE ---------------- */

function SoukGuide() {
  return (
    <section id="buy-guide" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-24">
      <h2 className="text-xl md:text-2xl font-bold mb-2">How to Buy Gold in Dubai — Souk Buying Guide</h2>
      <div className="prose prose-sm dark:prose-invert max-w-none text-foreground/90 space-y-3">
        <p>The <b>Dubai Gold Souk</b> in Deira is the world's largest gold market — over 380 retailers, 25+ tonnes of gold on display at any time, and rates regulated by the Dubai Multi Commodities Centre (DMCC). Whether you're buying a wedding set, an investment bar, or a single chain for your mother back home, follow this playbook.</p>
        <ol className="list-decimal pl-6 space-y-2">
          <li><b>Check the day's reference rate first.</b> Open this page on your phone — the live 24K and 22K AED/gram rate above is the wholesale benchmark every souk retailer uses. Walk in knowing the number.</li>
          <li><b>Inspect the hallmark.</b> Genuine UAE gold is stamped with the karat (916 for 22K, 750 for 18K) plus a Dubai or BIS hallmark. Reject anything unstamped.</li>
          <li><b>Negotiate the making charge — not the gold rate.</b> The metal price is fixed; making (labour) charges range from 5% (simple chains at souk veterans like Kanz) to 25% (intricate Damas designs). Always ask for the breakdown in writing.</li>
          <li><b>Get the original invoice.</b> Mandatory for VAT refund and for declaring at customs when leaving the country.</li>
          <li><b>Claim your 5% VAT refund at the airport.</b> Tourists spending more than AED 250 at participating retailers can scan their passport at any Planet Tax Free kiosk in DXB, AUH or SHJ before check-in. Refund is paid in cash or back to your card.</li>
          <li><b>Know the carry-home limits.</b> Indian passengers: 20 g (male) / 40 g (female) duty-free. Pakistani passengers: PKR 50,000 worth duty-free if abroad &gt; 6 months. Always declare — Indian and Pakistani customs use X-ray and personal scanning.</li>
          <li><b>Best time to buy.</b> Mid-week (Tue–Wed) before 2 PM is least crowded. Avoid the last 3 days of Ramadan and Eid week — rates and crowds both spike.</li>
        </ol>
        <p className="text-sm text-muted-foreground">
          <b>Pro tip:</b> Compare the souk's quoted 22K rate to our live <a href="#karat-table" className="text-primary hover:underline">UAE 22K per gram</a> above. If the difference is more than 3 AED/g <i>before</i> making charges, walk to the next shop. Competition between 380 retailers within 200 meters works in your favour.
        </p>
      </div>
    </section>
  );
}

/* ---------------- FAQ ---------------- */

function UaeFaq() {
  const schema = useMemo(() => faqPageSchema(UAE_FAQS), []);
  return (
    <section id="faq" className="mx-auto max-w-6xl px-4 py-8 scroll-mt-24">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <FaqSection faqs={UAE_FAQS} title="UAE Gold Rate — Frequently Asked Questions" />
    </section>
  );
}

/* ---------------- RELATED ---------------- */

function RelatedLinks() {
  return (
    <section id="related" className="mx-auto max-w-6xl px-4 pb-12 scroll-mt-24">
      <h2 className="text-lg md:text-xl font-bold mb-3">Compare gold rates in other countries</h2>
      <div className="grid gap-3 sm:grid-cols-3">
        <Link to="/pakistan" className="rounded-xl border border-border bg-card p-4 hover:shadow-md transition-shadow">
          <div className="text-2xl">🇵🇰</div>
          <div className="mt-1 font-semibold">Gold Rate in Pakistan</div>
          <div className="text-xs text-muted-foreground">Live PKR per tola — Karachi, Lahore, Islamabad +18 cities</div>
        </Link>
        <Link to="/india" className="rounded-xl border border-border bg-card p-4 hover:shadow-md transition-shadow">
          <div className="text-2xl">🇮🇳</div>
          <div className="mt-1 font-semibold">Gold Rate in India</div>
          <div className="text-xs text-muted-foreground">Live INR per 10g — Delhi, Mumbai, Chennai +17 cities</div>
        </Link>
        <Link to="/zakat-calculator" className="rounded-xl border border-border bg-card p-4 hover:shadow-md transition-shadow">
          <div className="text-2xl">🕌</div>
          <div className="mt-1 font-semibold">Full Zakat Calculator</div>
          <div className="text-xs text-muted-foreground">Gold + silver + cash + business assets in any currency</div>
        </Link>
      </div>
    </section>
  );
}
